import React,
{
    useCallback,
    useMemo,
    useState
} from 'react';
import { Input, Space } from 'antd';
import { useHistory } from 'react-router-dom';
import ColorPicker from '../../../components/common/colorPicker/ColorPicker';
import TagActions from '../../../components/common/tagActions/TagActions';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { toShortLocalDate } from '../../../helpers/dateHelper';
import {
    deleteTagAction,
    updateTagAction
} from '../../../redux/actions/tagActions';
import { alertService } from '../../../services';
import TagsSearch from '../../../components/tags/TagsSearch/TagsSearch';
import { PaginationContainer } from '../../../components/common/table/CustomTable.styles';
import CustomPagination from '../../../components/common/pagination/CustomPagination';
import ChangePageSize from '../../../components/common/pagination/ChangePageSize';
import { SpaceEnd, SubTitle } from '../../../styles/common.styles';
import { Tag } from '../../../redux/actions/tagActions/types';
import { ColorTile } from '../../../components/common/colorPicker/ColorPicker.styles';
import { TableData } from '../../../models/tableData/types';
import {
    EditableTagOption,
    TagDropdownOption,
    TagGroup,
    TagListContainer,
    TagTable,
    TagsTableSpace,
    UnselectAllButton,
    TableRow,
    TableRowLink,
}
    from './TagsTable.styles';
import { Color } from '../../../redux/actions/colorsActions/types';
import { TAG_NAME_MAX_LENGTH } from '../../../utils/tagUtils';
import { TagsTableProps } from './TagsTable.types';
import { usePermissions } from '../../../hooks/permissions';
import { AccountPermissionTypes } from '../../../models/permission/enums';

const TagsTable: React.FC<TagsTableProps> = ({
    tableData,
    totalCount,
    setSelectedTagIds,
    setTableData,
    selectedTagIds,
    fetchTags }) => {
    const [editingNames, setEditingNames] = useState<Record<number, string>>({});
    const [colorPopoverVisible, setColorPopoverVisible] = useState<Record<number, boolean>>({});
    const { tags } = useAppSelector((state) => state.tagReducer);
    const { getAccountRole } = usePermissions();
    const dispatch = useAppDispatch();
    const history = useHistory();

    const selectedEditableTags = useMemo(() => {
        return tags.filter(
            (t) => selectedTagIds.includes(t.id) && t.isEditable
        );
    }, [tags, selectedTagIds]);

    const canCreateUpdateDeleteTag = getAccountRole() === AccountPermissionTypes.ACCOUNT_ADMIN

    const changePageNumber = (pageNumber: number) => {
        const newTableData = { ...tableData, curPage: pageNumber };
        setTableData(newTableData);
        fetchTags(newTableData);
    };

    const changePageSize = (newPageSize: number) => {
        const newTableData = { ...tableData, pageSize: newPageSize, curPage: 1 };
        setTableData(newTableData);
        fetchTags(newTableData);
    };

    const handleCurrentPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const currentCount = event.currentTarget.value;
        if (/^-?\d*\.?\d*$/.test(currentCount) || currentCount === '') {
            setTableData({ ...tableData, curPage: +event.currentTarget.value });
            if (+currentCount > Math.ceil(totalCount / tableData.pageSize)) {
                setTimeout(() => {
                    const newCur = Math.ceil(totalCount / tableData.pageSize);
                    setTableData({ ...tableData, curPage: newCur });
                    changePageNumber(newCur);
                }, 1000);
            }
        }
        if (
            +event.currentTarget.value > 0 &&
            +event.currentTarget.value <= Math.ceil(totalCount / tableData.pageSize)
        ) {
            changePageNumber(+event.currentTarget.value);
        }
    };

    const handleSearchChange = useCallback((value: string) => {
        setTableData((prev) => ({ ...prev, searchString: value }));
    }, []);

    const handleSearchSubmit = useCallback((_e: React.KeyboardEvent<HTMLInputElement>) => {
        setTableData((prev) => {
            const newTableData = { ...prev, curPage: 1 };
            fetchTags(newTableData);
            return newTableData;
        });
    }, [fetchTags]);

    const handleSearchClear = useCallback(() => {
        setTableData((prev) => {
            const newTableData = { ...prev, searchString: '', curPage: 1 };
            fetchTags(newTableData);
            return newTableData;
        });
    }, [fetchTags]);

    const handleSelectAll = (e: any) => {
        if (e.target.checked) {
            setSelectedTagIds(tags.filter((t) => t.isEditable).map((t) => t.id));
        } else {
            setSelectedTagIds([]);
        }
    };

    const handleNameChange = (tagId: number, value: string) => {
        setEditingNames((prev) => ({ ...prev, [tagId]: value }));
    };

    const handleNameSubmit = async (t: Tag) => {
        const newName = editingNames[t.id]?.trim();
        if (!newName || newName === t.name) {
            setEditingNames((prev) => {
                const next = { ...prev };
                delete next[t.id];
                return next;
            });
            return;
        }
        const result = await dispatch(updateTagAction({ id: t.id, data: { name: newName, colorId: t?.color?.id! } }));
        if (updateTagAction.fulfilled.match(result)) {
            alertService.success('Tag updated successfully');
        }
        setEditingNames((prev) => {
            const next = { ...prev };
            delete next[t.id];
            return next;
        });
    };

    const handleColorChange = async (record: Tag, newColor: Color | null) => {
        if (newColor === record.color || newColor === null) {
            return;
        }
        const result = await dispatch(updateTagAction({ id: record.id, data: { name: record.name, colorId: newColor.id } }));
        if (updateTagAction.fulfilled.match(result)) {
            alertService.success('Tag color updated successfully');
        }
    };

    const handleBulkDelete = async () => {
        const results = await Promise.allSettled(
            selectedEditableTags.map((t) => dispatch(deleteTagAction(t.id)))
        );

        const failed = results
            .map((r, i) => ({ r, id: selectedEditableTags[i].id }))
            .filter(x => x.r.status === 'rejected')
            .map(x => x.id);

        if (failed.length) {
            alertService.error(`Failed to delete ${failed.length} tag(s)`);
        } else {
            alertService.success('Tags deleted successfully');
        }
        setSelectedTagIds([]);
        fetchTags(tableData);
    };

    const handleBulkChangeColor = async (newColor: Color) => {
        const results = await Promise.allSettled(
            selectedEditableTags.map((t) => dispatch(updateTagAction({ id: t.id, data: { name: t.name, colorId: newColor.id } })))
        );

        const failed = results
            .map((r, i) => ({ r, id: selectedEditableTags[i].id }))
            .filter(x => x.r.status === 'rejected')
            .map(x => x.id);

        if (failed.length) {
            alertService.error(`Failed to update color for ${failed.length} tag(s)`);
        } else {
            alertService.success('Tag colors updated successfully');
        }
        setSelectedTagIds([]);
        fetchTags(tableData);
    };

    const columnToSortExpression: Record<string, string> = {
        name: 'Name',
        color: 'ColorName',
        productCount: 'ProductCount',
        createdOn: 'CreatedOn',
        createdByName: 'CreatedByName',
    };

    const handleTableChange = (_pagination: any, _filters: any, sorter: any) => {
        const sortDir = sorter.order === 'descend' ? 'desc' : 'asc';
        const sortExpr = sorter.order ? (columnToSortExpression[sorter.field as string] ?? null) : null;
        const newTableData = { ...tableData, sortDirection: sortDir, sortExpression: sortExpr, curPage: 1 } as TableData;
        setTableData(newTableData);
        fetchTags(newTableData);
    };

    const tagDataSource = useMemo(() => {
        return tags.map((t) => ({
            key: t.id,
            ...t,
        }));
    }, [tags]);

    const tagColumns = [
        {
            title: 'Tag',
            dataIndex: 'name',
            key: 'name',
            width: 340,
            sorter: true,
            sortDirections: ['ascend', 'descend'] as const,
            showSorterTooltip: false,
            render: (name: string, record: Tag) => {
                if (!record.isEditable || !canCreateUpdateDeleteTag) {
                    return (
                        <Input
                            style={{ padding: '4px', paddingLeft: '0' }}
                            value={editingNames[record.id] ?? name}
                            onChange={(e) => handleNameChange(record.id, e.target.value)}
                            onPressEnter={() => handleNameSubmit(record)}
                            onBlur={() => handleNameSubmit(record)}
                            bordered={false}
                            readOnly
                        />
                    )
                }
                return (
                    <Input
                        style={{ padding: '4px 10px' }}
                        value={editingNames[record.id] ?? name}
                        onChange={(e) => handleNameChange(record.id, e.target.value)}
                        onPressEnter={() => handleNameSubmit(record)}
                        onBlur={() => handleNameSubmit(record)}
                        maxLength={TAG_NAME_MAX_LENGTH}
                    />
                );
            },
        },
        {
            title: '',
            dataIndex: 'color',
            key: 'color',
            width: 160,
            render: (tagColor: Color, record: Tag) => {
                if (!record.isEditable || !canCreateUpdateDeleteTag) {
                    return <TagDropdownOption>
                        <ColorTile color={tagColor.display} isReadOnly={true} />
                        {tagColor.name}
                    </TagDropdownOption>

                }
                return (
                    <EditableTagOption>
                        <ColorPicker
                            color={tagColor}
                            setColor={(newColor: Color | null) => handleColorChange(record, newColor)}
                            popoverVisible={!!colorPopoverVisible[record.id]}
                            setPopoverVisible={(visible) =>
                                setColorPopoverVisible((prev) => ({ ...prev, [record.id]: visible }))
                            }
                        />
                    </EditableTagOption>
                );
            },
        },
        {
            title: 'Products',
            dataIndex: 'productCount',
            key: 'productCount',
            width: 90,
            sorter: true,
            sortDirections: ['ascend', 'descend'] as const,
            showSorterTooltip: false,
            render: (count: number | undefined, record: Tag) => (
                count ?
                    <TableRowLink className="text-bold"
                        onClick={() => history.push(`/tags-info/${record.id}`)}
                    >
                        {count}
                    </TableRowLink> :
                    <TableRow className="text-bold">{0}</TableRow>
            ),
        },
        {
            title: 'Date Added',
            dataIndex: 'createdOn',
            key: 'createdOn',
            width: 120,
            sorter: true,
            sortDirections: ['ascend', 'descend'] as const,
            showSorterTooltip: false,
            render: (date: string | undefined, record: Tag) =>
                <TableRow>{record.isEditable && date ? toShortLocalDate(date) : '–'}</TableRow>,
        },
        {
            title: 'Added By',
            dataIndex: 'createdByName',
            key: 'createdByName',
            width: 160,
            sorter: true,
            sortDirections: ['ascend', 'descend'] as const,
            showSorterTooltip: false,
            render: (name: string | undefined) =>
                <TableRow>{name ?? '–'}</TableRow>,
        },
    ];

    const rowSelection = {
        selectedRowKeys: selectedTagIds,
        onChange: (selectedRowKeys: React.Key[]) => {
            setSelectedTagIds(selectedRowKeys as number[]);
        },
        renderCell: (_checked: boolean, record: Tag, _index: number, originNode: React.ReactNode) => {
            return record.isEditable && canCreateUpdateDeleteTag ? originNode : null;
        },
    };
    return <TagListContainer>
        {canCreateUpdateDeleteTag && <TagActions
            selectedCount={selectedEditableTags.length}
            selectedTagNames={selectedEditableTags.map((t) => t.name)}
            onBulkDelete={handleBulkDelete}
            onBulkChangeColor={handleBulkChangeColor}
            disabled={selectedEditableTags.length === 0}
        />}

        <TagsTableSpace direction="horizontal" className={canCreateUpdateDeleteTag ? "shift-down" : ""}>
            <Space direction="vertical" size={16}>
                <TagGroup>
                    <TagsSearch
                        searchString={tableData.searchString}
                        changeSearch={handleSearchChange}
                        cleanFilter={handleSearchClear}
                        handleSearchSubmit={handleSearchSubmit} />
                    <SubTitle>
                        {totalCount} tags
                    </SubTitle>
                    {canCreateUpdateDeleteTag && (
                        <UnselectAllButton onClick={() => handleSelectAll({ target: { checked: false } })}>
                            Unselect all
                        </UnselectAllButton>
                    )}
                </TagGroup>
            </Space>
            <SpaceEnd>
                <ChangePageSize changePageSize={changePageSize} pageSize={tableData.pageSize} />
                <PaginationContainer style={{ marginLeft: 16 + `px` }}>
                    <CustomPagination
                        page={tableData.curPage}
                        total={totalCount}
                        pageSize={tableData.pageSize}
                        changePageNumber={changePageNumber}
                        handleCurrentPage={handleCurrentPage}
                    />
                </PaginationContainer>
            </SpaceEnd>
        </TagsTableSpace>
        <TagTable
            readOnly={!canCreateUpdateDeleteTag}
            onChange={handleTableChange}
            rowSelection={canCreateUpdateDeleteTag ? {
                type: 'checkbox',
                ...rowSelection,
                columnWidth: 16,
            } : undefined}
            dataSource={tagDataSource}
            columns={tagColumns}
            pagination={false}
        />
        <SpaceEnd>
            <PaginationContainer style={{ marginLeft: 16 + `px` }}>
                <CustomPagination
                    page={tableData.curPage}
                    total={totalCount}
                    pageSize={tableData.pageSize}
                    changePageNumber={changePageNumber}
                    handleCurrentPage={handleCurrentPage}
                />
            </PaginationContainer>
        </SpaceEnd>
    </TagListContainer>
}
export default TagsTable;
